import { ChevronDown, ChevronRight } from 'lucide-react';

interface OrgNodeProps {
  name: string;
  title: string;
  shortName?: string;
  department: string;
  colorClass: string;
  variant: 'owner' | 'csuite' | 'agent';
  isCollapsible?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
}

function getInitials(name: string): string {
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const AVATAR_SIZES = {
  owner: 'w-[52px] h-[52px] text-lg',
  csuite: 'w-[44px] h-[44px] text-sm',
  agent: 'w-[36px] h-[36px] text-xs',
};

export function OrgNode({
  name,
  title,
  shortName,
  department,
  colorClass,
  variant,
  isCollapsible = false,
  isExpanded = false,
  onToggle,
}: OrgNodeProps) {
  const initials = getInitials(name);
  const avatarSize = AVATAR_SIZES[variant];

  const ringClass = variant === 'owner'
    ? 'ring-2 ring-amber-500'
    : variant === 'csuite'
      ? 'ring-2 ring-accent-purple'
      : '';

  const displayTitle = shortName && shortName !== name ? shortName : title;

  return (
    <div
      className={`flex flex-col items-center gap-1 ${isCollapsible ? 'cursor-pointer select-none' : ''}`}
      onClick={isCollapsible ? onToggle : undefined}
      role={isCollapsible ? 'button' : undefined}
      tabIndex={isCollapsible ? 0 : undefined}
      onKeyDown={
        isCollapsible
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle?.();
              }
            }
          : undefined
      }
    >
      {/* Avatar */}
      <div
        className={`${avatarSize} ${colorClass} ${ringClass} rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0`}
      >
        {initials}
      </div>

      {/* Name + Title */}
      <div className="text-center max-w-[120px]">
        <p className="text-xs font-semibold text-text-primary leading-tight truncate">{name}</p>
        <p className="text-[10px] text-text-muted leading-tight truncate">{displayTitle}</p>
      </div>

      {/* Expand indicator */}
      {isCollapsible ? (
        <div className="text-text-muted">
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </div>
      ) : null}
    </div>
  );
}

export default OrgNode;
